// Renders Sim.terrain: destructible trees / rocks / boulders / spires (instanced, toon + outlines) and blast craters.
// Reads sim state only; destruction feedback comes from propHit / propBreak / crater events.
import * as THREE from 'three/webgpu';
import { SHADOW } from './camFrame';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { canyonBoulder, canyonColumn, canyonMaterial } from './canyon';
import { buildTree, TreePart } from './trees';
import { BUSH_K, buildBushVariants, buildTreeVariants, coreShadowMask, FloraShape } from './flora';
import type { Sim } from '../core/sim';
import { DT } from '../core/sim';
import { Prop, PropKind, MAX_CRATERS, MAX_SCORCH, propCounts, isFlora } from '../core/terrain';
import { ARENA_R } from '../core/sim';
import { instancedOutline, outlineGeometry, outlineMaterial, RenderStyle, toonMaterial } from './toon';
import { cameraPosition, float, mix, positionWorld, select, smoothstep, uniform, vec3 } from 'three/tsl';
import { easeShape, easeStep, GhostDepth, ghostBodyMaterial, ghostInkMaterial, ghostInkTwin, ghostTwin, setGhostCount, nearHeroLine, projectedBox } from './ghost';
import type { Fx } from './fx';
import type { GroundDeform } from './ground';
import type { PropSkin } from './mapkit';
import { GROUND_HALF } from './ground';
import { ChunkGrid, CULL, GroundBox, inBox, viewBox } from './chunkCull';
/** shadow camera half-size (timeOfDay.ts SHADOW: ±30 m around the hero, more on far camera presets) + the longest shadow a
 *  tall prop throws into it */
const shadowReach = (): number => SHADOW.half + 32;

const _gb = new Float32Array(4);
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _e = new THREE.Euler(), _q2 = new THREE.Quaternion(), _ax = new THREE.Vector3();
/** deterministic per-prop jitter 0..1 (render-only looks: width, lean, hue) */
const jit = (p: Prop, k: number) => { const h = Math.sin(p.x * 12.9898 + p.z * 78.233 + k * 37.719) * 43758.5453; return h - Math.floor(h); };

/** Real-world proportions vs the ~2 m hero: trees 9–14 m, rock spires ~20–33 m. Render-only; sim footprints unchanged. */
export const TREE_K = 2.25, SPIRE_K = 1.85;

/** Hero focus point for the occlusion cutaway (updated each frame from view.ts). */
export const cutFocus = uniform(new THREE.Vector3());
/** Portrait play-area cutaway zone around the hero (x half width, y toward the camera, z away from it, w strength 0..1). */
export const cutZone = uniform(new THREE.Vector4(0, 0, 0, 0));
/** Occlusion cutaway, spatial part (per fragment): 0 = keep, 1 = fully cut. Prop fragments above knee height that sit
 *  between the camera and the hero (a small cylinder around the camera→hero line) or, on portrait screens, that hide the
 *  play area around the hero are cut, so tall trees / spires never hide the player. The cut is drawn by the ghost twins
 *  (ghost.ts): alpha blended down to the prop's minimum alpha and eased in / out per prop. It used to be a screen-door
 *  dither, which a phone's low render scale blew up into a coarse checkerboard (owner 2026-11-02). */
function cutawayS() {
  const a = cutFocus.add(vec3(0, 1, 0));
  const ab = cameraPosition.sub(a);
  const t = positionWorld.sub(a).dot(ab).div(ab.dot(ab)).clamp(0, 1);
  const d = positionWorld.sub(a.add(ab.mul(t))).length();
  // owner 2026-11-01: 'make sure every asset I can interact with or touch is visible' — the cut never removes a prop
  // entirely (a ghost keeps its minimum alpha, GHOST_MIN below), and the base up to ~1.6 m stays solid so the footprint you
  // bump into always reads.
  // owner 2026-11-02: 'too obvious… even when I walk on a shadow… the circle is visible' — the cut is a small zone that
  // just frames Aren (1.3–2.6 m from the camera→hero line, was 3.2–5.5 m) and never applies in the shadow pass, so
  // shadows stay whole.
  const cyl = smoothstep(float(1.3), float(2.6), d).max(smoothstep(float(0.04), float(0.0), t));
  // owner 2026-11-02 (phone portrait): 'the bottom third of the screen is covered by foliage' — on tall screens the cut also
  // covers the play area on the camera side of the hero. Each fragment is projected along the view ray onto the ground
  // plane; when the ground point it hides lies inside a rounded-rectangle zone around the hero (cutZone: x = half width,
  // y = reach toward the camera, z = reach away from it, w = strength, set per frame by view.ts from the visible ground),
  // the fragment is cut too. Landscape screens keep w = 0 (only the small hero cut above).
  const cam = cameraPosition, ray = positionWorld.sub(cam);
  const g = cam.add(ray.mul(cutFocus.y.sub(cam.y).div(ray.y.min(float(-0.001)))));
  const off = g.sub(cutFocus);
  const ex = off.x.div(cutZone.x.max(0.01)).abs(), ez = off.z.div(select(off.z.greaterThan(0), cutZone.y, cutZone.z).max(0.01)).abs();
  const e = ex.mul(ex).mul(ex).mul(ex).add(ez.mul(ez).mul(ez).mul(ez)).pow(0.25); // superellipse: fills the screen's corners
  const zone = mix(float(1), smoothstep(float(0.72), float(1.0), e), cutZone.w);
  return float(1).sub(cyl.min(zone)).mul(float(1).sub(smoothstep(float(1.6), float(1.0), positionWorld.y)));
}

/** Minimum alpha of a fully cut ghost per kind. Low props barely block the view (owner report 2026-11-02: a boulder's top
 *  half was the glitch): boulders below ~hero height are never cut, taller ones and bush tops fade only a little; trees and
 *  spires fade to a light ghost (the old dither kept ~34–45% of the pixels). Rocks are never cut. */
const GHOST_MIN: Record<PropKind, number> = { tree: 0.3, spire: 0.3, bush: 0.6, boulder: 0.65, rock: 1 };
/** boulders whose top is lower than this above their base (m) are never cut */
const BOULDER_CUT_H = 2.3;
/** ghost twin capacity per prop set (overflow simply stays opaque) */
const GHOST_CAP = 24;

function lumpy(detail: number, seed: number): THREE.BufferGeometry {
  const g = new THREE.DodecahedronGeometry(1, detail);
  const pos = g.attributes.position as THREE.BufferAttribute; const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    v.multiplyScalar(0.82 + 0.3 * Math.abs(Math.sin(v.x * 3.1 + seed + v.y * 1.7 + v.z * 2.3)));
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  g.computeVertexNormals();
  return g;
}

/** Stacked, slightly offset rock drums: a tall weathered spire, unit radius, unit height (scaled per prop). */
function spireGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const drums = 5;
  for (let k = 0; k < drums; k++) {
    const r0 = 1 - k * 0.12, r1 = 1 - (k + 1) * 0.12 + 0.04;
    const g = new THREE.CylinderGeometry(r1, r0, 1 / drums + 0.01, 7, 1);
    g.rotateY(k * 0.6); g.translate(Math.sin(k * 2.1) * 0.05, (k + 0.5) / drums, Math.cos(k * 1.7) * 0.05);
    parts.push(g.toNonIndexed());
  }
  const cap = new THREE.ConeGeometry(0.5, 0.12, 7); cap.translate(0, 1.04, 0); parts.push(cap.toNonIndexed());
  const out = mergeGeometries(parts, false)!; out.computeVertexNormals(); return out;
}

/** smooth, puffy leaf mass (owner 2026-11-01: 'more rounded, higher polygon, smoother'): welded high-detail spheres with
 *  soft lumps, so the toon ramp shades them as round clumps instead of facets */
function puff(r: number, x: number, y: number, z: number, seed: number): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(r, 3); g.deleteAttribute('normal'); g.deleteAttribute('uv');
  const w = mergeVertices(g, 1e-4), pos = w.attributes.position as THREE.BufferAttribute, v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i); const n = v.clone().normalize();
    const lump = 1 + 0.09 * Math.sin(n.x * 5.1 + seed) * Math.sin(n.y * 4.3 + seed * 2) * Math.sin(n.z * 4.7 + seed * 3);
    v.multiplyScalar(lump); if (v.y < -r * 0.55) v.y = -r * 0.55 + (v.y + r * 0.55) * 0.35; // flatter underside
    pos.setXYZ(i, v.x + x, v.y + y, v.z + z);
  }
  w.computeVertexNormals(); return w;
}
const CLUMPS: [number, number, number, number][] = [
  [1.05, 0, 2.55, 0], [0.82, 0.62, 2.2, 0.22], [0.8, -0.58, 2.22, -0.28], [0.72, 0.12, 2.15, -0.68], [0.7, -0.1, 2.25, 0.7],
  [0.72, 0.06, 3.22, 0.08], [0.58, 0.48, 2.95, -0.3], [0.56, -0.44, 2.98, 0.32],
];
function treeCanopy(): THREE.BufferGeometry {
  const out = mergeGeometries(CLUMPS.map(([r, x, y, z], k) => puff(r, x, y, z, k * 1.7)), false)!;
  out.computeBoundingSphere(); return out;
}
/** individual leaves: small pointed leaf blades (double-sided) sprouting from the clump surfaces, tilted outward */
function treeLeaves(count = 260): THREE.BufferGeometry {
  const leaf = new THREE.BufferGeometry();
  // pointed oval blade along +Y, 0.34 m long, with a centre fold so it catches light
  leaf.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0.09, 0.12, 0.02, 0, 0.34, 0, -0.09, 0.12, 0.02, 0, 0.12, -0.03], 3));
  leaf.setIndex([0, 1, 4, 1, 2, 4, 2, 3, 4, 3, 0, 4]);
  const parts: THREE.BufferGeometry[] = []; const q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), n = new THREE.Vector3(), m = new THREE.Matrix4();
  let seed = 1; const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let i = 0; i < count; i++) {
    const [r, x, y, z] = CLUMPS[Math.floor(rnd() * CLUMPS.length)];
    n.set(rnd() * 2 - 1, rnd() * 1.6 - 0.5, rnd() * 2 - 1).normalize();
    q.setFromUnitVectors(up, n.clone().add(new THREE.Vector3(0, 0.5, 0)).normalize());
    q.multiply(new THREE.Quaternion().setFromAxisAngle(up, rnd() * 6.28));
    const sc = 0.8 + rnd() * 0.6;
    m.compose(new THREE.Vector3(x + n.x * r * 0.93, y + n.y * r * 0.93, z + n.z * r * 0.93), q, new THREE.Vector3(sc, sc, sc));
    parts.push(leaf.clone().applyMatrix4(m));
  }
  const out = mergeGeometries(parts, false)!; out.computeVertexNormals(); return out;
}
/** trunk with root flare and two limbs reaching into the canopy */
function treeTrunk(): THREE.BufferGeometry {
  const t = new THREE.CylinderGeometry(0.15, 0.26, 2.2, 14, 6).translate(0, 1.1, 0);
  const pos = t.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) { const y = pos.getY(i); const k = y < 0.35 ? 1 + (0.35 - y) * 0.9 : 1; pos.setX(i, pos.getX(i) * k); pos.setZ(i, pos.getZ(i) * k); }
  const b1 = new THREE.CylinderGeometry(0.06, 0.1, 1.0, 10).translate(0, 0.5, 0).rotateZ(-0.7).translate(0.05, 1.6, 0);
  const b2 = new THREE.CylinderGeometry(0.05, 0.09, 0.9, 10).translate(0, 0.45, 0).rotateX(0.65).translate(0, 1.75, 0.05);
  const out = mergeGeometries([t, b1, b2].map((g) => { g.deleteAttribute('uv'); return g; }), false)!; out.computeVertexNormals(); return out;
}

interface Batch { kind: PropKind; v: number; meshes: THREE.InstancedMesh[]; outlines: THREE.InstancedMesh[]; shape: FloraShape | null; n: number; /** drawn at least once (its pipelines are compiled) */ warm?: boolean;
  /** cutaway ghost twins (one per mesh part / outline), their per-instance fade depth and fill count; local bounds of the
   *  shape (top height, horizontal radius) for the CPU cut test */
  ghosts: THREE.InstancedMesh[]; ghostInk: THREE.InstancedMesh[]; gd: GhostDepth | null; gn: number; top: number; bot: number; rad: number }
/** a prop drawn by a ghost twin this frame */
interface GhostItem { p: Prop; b: Batch; depth: number; dist: number }

export class PropsView {
  private batches: Batch[] = [];
  private craterDecal: THREE.InstancedMesh;
  private craterRim: THREE.InstancedMesh;
  private craterOutline: THREE.InstancedMesh | null = null;
  private scorch: THREE.InstancedMesh;
  private embers: THREE.InstancedMesh;

  private stamped = new WeakSet<object>();
  /** shapes handed to the debris system for fracturing */
  sources!: { rock: THREE.BufferGeometry; boulder: THREE.BufferGeometry; spire: THREE.BufferGeometry; tree: THREE.Object3D; treeParts: TreePart[]; cel: boolean; trees: FloraShape[]; bushes: FloraShape[] };
  /** tree / bush variant shapes (index = Prop.v) */
  readonly trees: FloraShape[]; readonly bushes: FloraShape[];
  private byKind = new Map<PropKind, Batch[]>();

  /** MAP SYSTEM: break dust colours per archetype (map skin) */
  private dust: NonNullable<PropSkin['dust']>;
  private shadows = false;
  private scene: THREE.Scene;
  /** skin = the map's archetype reskin (src/maps/<id>/render.ts), cap = the map's prop counts (core spec) */
  constructor(scene: THREE.Scene, style: RenderStyle, shadows: boolean, private ground: GroundDeform, highQ = false, skin: PropSkin = {}, cap?: Record<PropKind, number>) {
    this.scene = scene; this.dust = skin.dust ?? {}; this.shadows = shadows;
    const cel = style === 'cel';
    const mat = (c: number) => cel ? toonMaterial({ color: c }) : new THREE.MeshStandardNodeMaterial({ color: c, roughness: 0.9, flatShading: true });
    // fixed capacities (a fresh Sim per run generates a new layout; terrain.ts never exceeds these)
    const CAP = cap ?? propCounts(ARENA_R); // exactly what the terrain places
    const count = (k: PropKind) => CAP[k];
    const mk = (kind: PropKind, parts: [THREE.BufferGeometry, THREE.Material, number][], v = 0, shape: FloraShape | null = null) => {
      const n = Math.max(1, count(kind));
      // cutaway (bushes too — owner 2026-11-02: bush tops fade in the portrait zone): the opaque sets carry no mask any more;
      // props near the cut are moved to the ghost twins each frame (sync)
      const cut = GHOST_MIN[kind] < 1, gcap = Math.min(n, GHOST_CAP), gd = cut ? new GhostDepth(gcap) : null;
      const b: Batch = { kind, v, meshes: [], outlines: [], shape, n: 0, ghosts: [], ghostInk: [], gd, gn: 0, top: -Infinity, bot: Infinity, rad: 0 };
      for (const [g, m, thick] of parts) {
        const fol = !!g.getAttribute('core'); // flora foliage: only the solid lobe cores cast shadows
        if (fol) (m as THREE.NodeMaterial).maskShadowNode = coreShadowMask();
        if (!g.boundingBox) g.computeBoundingBox();
        const bb = g.boundingBox!;
        b.top = Math.max(b.top, bb.max.y); b.bot = Math.min(b.bot, bb.min.y); b.rad = Math.max(b.rad, Math.hypot(Math.max(-bb.min.x, bb.max.x), Math.max(-bb.min.z, bb.max.z)));
        const im = new THREE.InstancedMesh(g, m, n);
        // MAP-FIRE: per-instance tint (white = untouched; burning trees / scorched rocks blacken toward char)
        im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3).fill(1), 3);
        im.castShadow = shadows; im.receiveShadow = shadows; im.frustumCulled = false; im.count = 0;
        scene.add(im); b.meshes.push(im);
        const gm = gd ? ghostTwin(im, ghostBodyMaterial(m, cutawayS(), gd.node), gcap, true, b.meshes.length - 1) : null;
        if (gm) { scene.add(gm); b.ghosts.push(gm); }
        if (cel && thick > 0) { const o = instancedOutline(im, thick, n); o.count = 0; scene.add(o); b.outlines.push(o);
          if (gm && gd) { const gi = ghostInkTwin(gm, o.geometry, ghostInkMaterial(o.material as THREE.Material, cutawayS(), gd.node), gcap); scene.add(gi); b.ghostInk.push(gi); } }
      }
      this.batches.push(b);
      const list = this.byKind.get(kind) ?? []; list[v] = b; this.byKind.set(kind, list);
    };
    // owner 2026-11-01: picked tree design E (painted canopy) from the tree lab — trunk, solid lobe cores, painted leaf cards
    // owner 2026-11-02 'several various versions of the trees… add some bushes too': one instanced set per variant
    // (trunk + merged foliage), built in flora.ts; the classic variant is the owner-picked design E
    this.trees = skin.trees?.() ?? buildTreeVariants(style); this.bushes = skin.bushes?.() ?? buildBushVariants();
    this.trees.forEach((f, v) => mk('tree', f.parts.map((tp) => [tp.geo, tp.mat, tp.outline] as [THREE.BufferGeometry, THREE.Material, number]), v, f));
    this.bushes.forEach((f, v) => mk('bush', f.parts.map((tp) => [tp.geo, tp.mat, tp.outline] as [THREE.BufferGeometry, THREE.Material, number]), v, f));
    // bush shadows only on high quality (they are low to the ground; saves a shadow draw per variant)
    if (!shadows || !highQ) for (const b of this.byKind.get('bush') ?? []) for (const im of [...b.meshes, ...b.ghosts]) im.castShadow = false;
    const treeParts = this.trees[0].parts;
    void buildTree;
    // the toppling copy uses EVERY part of the standing tree with the same materials (it used to drop the painted leaf
    // cards and the trunk outline, so fallen trees looked different — owner 2026-11-02)
    void treeTrunk; void treeCanopy; void treeLeaves;
    // canyon sandstone (owner: 'canyon style rock structures'): strata in world space, smooth eroded shapes
    const rockParts = skin.rock?.() ?? [[canyonBoulder(1, 2), canyonMaterial(style, 1.02, false), 0.05]];
    const boulderParts = skin.boulder?.() ?? [[canyonBoulder(2, 3), canyonMaterial(style, 0.97, false), 0.07]];
    const rockGeo = rockParts[0][0], boulderGeo = boulderParts[0][0];
    mk('rock', rockParts);
    mk('boulder', boulderParts);
    void lumpy;
    const treeObj = new THREE.Group();
    for (const tp of treeParts) {
      const m = new THREE.Mesh(tp.geo, tp.mat); m.castShadow = true; treeObj.add(m); // foliage: cores only (maskShadowNode)
      if (cel && tp.outline > 0) treeObj.add(new THREE.Mesh(outlineGeometry(tp.geo), outlineMaterial(tp.outline)));
    }
    // a clean tapered column fractures better than the stacked-drum render mesh
    // MAP-DESTRUCTION (owner 2026-11-02 'much more realistic'): the debris now breaks a lower-res copy of the very column
    // that is drawn (same ledges / grooves / grass top), so the pieces match the spire instead of a plain prism
    const spireParts = skin.spire?.();
    const spireSolid = spireParts ? spireParts[0][0] : canyonColumn(3, { tiers: 2, taper: 0.3, groove: 0.22, radial: 18, rows: 12, dome: 0.1 });
    this.sources = { rock: rockGeo, boulder: boulderGeo, spire: spireSolid, tree: treeObj, treeParts, cel, trees: this.trees, bushes: this.bushes };
    mk('spire', spireParts ?? [[canyonColumn(3, { tiers: 2, taper: 0.3, groove: 0.22, radial: 36, rows: 30, dome: 0.1 }), canyonMaterial(style), 0.12]]);
    void spireGeometry;

    // craters: scorched decal + raised rim
    const tex = (() => {
      const S = 128, c = document.createElement('canvas'); c.width = c.height = S; const g = c.getContext('2d')!;
      const grd = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
      grd.addColorStop(0, 'rgba(40,28,20,0.92)'); grd.addColorStop(0.45, 'rgba(70,50,34,0.85)');
      grd.addColorStop(0.72, 'rgba(120,92,62,0.7)'); grd.addColorStop(0.86, 'rgba(150,120,84,0.45)'); grd.addColorStop(1, 'rgba(150,120,84,0)');
      g.fillStyle = grd; g.fillRect(0, 0, S, S);
      // radial cracks
      g.strokeStyle = 'rgba(25,16,10,0.6)'; g.lineWidth = 2;
      for (let k = 0; k < 9; k++) {
        const a = (k / 9) * Math.PI * 2 + Math.sin(k * 7) * 0.3; g.beginPath(); g.moveTo(S / 2 + Math.cos(a) * S * 0.18, S / 2 + Math.sin(a) * S * 0.18);
        g.lineTo(S / 2 + Math.cos(a + 0.1) * S * 0.36, S / 2 + Math.sin(a + 0.1) * S * 0.36); g.lineTo(S / 2 + Math.cos(a - 0.05) * S * 0.47, S / 2 + Math.sin(a - 0.05) * S * 0.47); g.stroke();
      }
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    const decalMat = new THREE.MeshBasicNodeMaterial({ map: tex, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    this.craterDecal = new THREE.InstancedMesh(new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2), decalMat, MAX_CRATERS);
    this.craterDecal.frustumCulled = false; this.craterDecal.count = 0; this.craterDecal.renderOrder = 1;
    const rimGeo = new THREE.TorusGeometry(1, 0.14, 5, 28).rotateX(Math.PI / 2);
    this.craterRim = new THREE.InstancedMesh(rimGeo, mat(0x9c7a52), MAX_CRATERS);
    this.craterRim.frustumCulled = false; this.craterRim.count = 0; this.craterRim.receiveShadow = shadows;
    // craters are carved into the deformable ground now (ground.ts); decal/rim meshes kept only as a fallback path
    void this.craterOutline;

    // burned grass: irregular char decal + additive ember glow that cools with age
    const blotch = (ember: boolean) => {
      const S = 128, c = document.createElement('canvas'); c.width = c.height = S; const g = c.getContext('2d')!;
      for (let k = 0; k < 14; k++) {
        const a = k * 2.399, d = (k % 5) * S * 0.07, x = S / 2 + Math.cos(a) * d, y = S / 2 + Math.sin(a) * d, r = S * (0.16 + ((k * 37) % 11) / 60);
        const grd = g.createRadialGradient(x, y, 0, x, y, r);
        if (ember) { grd.addColorStop(0, 'rgba(255,170,60,0.55)'); grd.addColorStop(0.5, 'rgba(255,90,20,0.2)'); grd.addColorStop(1, 'rgba(255,60,0,0)'); }
        else { grd.addColorStop(0, 'rgba(28,20,14,0.55)'); grd.addColorStop(0.6, 'rgba(48,34,20,0.3)'); grd.addColorStop(1, 'rgba(60,44,26,0)'); }
        g.fillStyle = grd; g.fillRect(0, 0, S, S);
      }
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
    };
    const plane = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
    this.scorch = new THREE.InstancedMesh(plane, new THREE.MeshBasicNodeMaterial({ map: blotch(false), transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 }), MAX_SCORCH);
    this.scorch.frustumCulled = false; this.scorch.count = 0; this.scorch.renderOrder = 0;
    this.embers = new THREE.InstancedMesh(plane, new THREE.MeshBasicNodeMaterial({ map: blotch(true), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false }), MAX_SCORCH);
    this.embers.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX_SCORCH * 3), 3);
    this.embers.frustumCulled = false; this.embers.count = 0; this.embers.renderOrder = 2;
    scene.add(this.embers); // char is painted into the ground map; only the glowing embers are separate
  }

  /** MAP SYSTEM: remove every mesh this view added (map change); shared cached materials are left alone */
  dispose(): void {
    // PERF PASS 3 (memory): the batch geometries (prop kinds x variants, outline shells) and this view's own decal textures
    // were kept alive on the GPU across map switches (+28 geometries per switch pair in ?leakcheck); the next map builds new ones
    for (const b of this.batches) for (const im of [...b.meshes, ...b.outlines]) { this.scene.remove(im); im.geometry.dispose(); im.dispose(); }
    // cutaway ghost twins (ghost.ts): they share the batch geometry (disposed above) but own cloned transparent materials,
    // and each body twin carries a depth pre-pass child with its own material clone
    const disposeMat = (m: THREE.Material | THREE.Material[]) => { for (const x of Array.isArray(m) ? m : [m]) x.dispose(); };
    for (const b of this.batches) for (const im of [...b.ghosts, ...b.ghostInk]) {
      this.scene.remove(im);
      for (const c of im.children) { const cm = c as THREE.InstancedMesh; disposeMat(cm.material); cm.dispose(); }
      disposeMat(im.material); im.dispose();
    }
    for (const im of [this.embers, this.scorch, this.craterDecal, this.craterRim]) { this.scene.remove(im); im.geometry.dispose(); (im.material as THREE.MeshBasicNodeMaterial).map?.dispose(); im.dispose(); }
    this.batches = []; this.byKind.clear();
  }

  /** BIGMAP chunk culling (owner 2026-11-02: arena 160 m, ~1100 props): props bucketed into 24 m chunks per layout */
  private grid = new ChunkGrid(GROUND_HALF, 24);
  private gridProps: Prop[] | null = null;
  private box: GroundBox = { x0: 0, z0: 0, x1: 0, z1: 0 };
  private readonly px = (i: number) => this.gridProps![i].x;
  private readonly pz = (i: number) => this.gridProps![i].z;
  /** QA / perf overlay: props considered (in visible chunks) last frame, and drawn */
  readonly cullStats = { considered: 0, drawn: 0, total: 0 };

  /** cutaway ghosts: eased cut amount per prop (0..1, only props > 0 are kept), double-buffered; props drawn as ghosts */
  private cutA = new Map<Prop, number>(); private cutB = new Map<Prop, number>();
  private ghostList: GhostItem[] = []; private lastT = 0;
  /** QA / perf overlay: props drawn as ghosts last frame */
  get ghostCount(): number { return this.ghostList.length; }

  /** Could prop p (its transform in _p / _s, from matrixFor) have fragments in the cut? Rough but generous: a prop it misses
   *  is simply not faded, a prop it wrongly takes is drawn by a ghost twin at alpha 1. */
  private mayCut(b: Batch, p: Prop, cam: THREE.Vector3, F: THREE.Vector3, Z: THREE.Vector4): boolean {
    const y0 = _p.y + b.bot * _s.y, y1 = _p.y + b.top * _s.y;
    if (y1 < 1.0) return false; // the cut never reaches below 1 m
    if (p.kind === 'boulder' && y1 - y0 < BOULDER_CUT_H) return false; // low boulders: never (they hardly block the view)
    const R = b.rad * Math.max(_s.x, _s.z) + 0.18 * (y1 - y0); // + lean / sway / rotation slack
    if (nearHeroLine(cam, F, p.x, p.z, R, y0, y1, 2.6 + 1.2)) return true;
    if (Z.w <= 0.001 || !projectedBox(cam, F.y, p.x, p.z, R, Math.max(y0, 1.0), y1, _gb)) return false;
    return _gb[1] > F.x - Z.x && _gb[0] < F.x + Z.x && _gb[3] > F.z - Z.z && _gb[2] < F.z + Z.y;
  }

  /** matrix (from matrixFor) + char / hue tint of prop p into slot n of meshes (a batch's opaque set or its ghost twins) */
  private writeInstance(meshes: THREE.InstancedMesh[], n: number, p: Prop, b: Batch, t: number): void {
    // char tint: dark sooty brown-grey (keeps its cel shading) / soot-dark stone (a burning tree flickers a little warmer)
    const c = p.char, warm = p.burn > 0 ? 0.06 * (1 + Math.sin(t * 13 + p.x)) * (1 - c * 0.5) : 0;
    const cr = 1 - c * 0.62 + warm, cg = 1 - c * 0.69 + warm * 0.4, cb = 1 - c * 0.73;
    // foliage hue per plant (render-only): a little lighter / darker, warmer / cooler, on the variant's base tint
    let hr = 1, hg = 1, hb = 1;
    if (b.shape) {
      const v = 0.92 + 0.16 * jit(p, 3), w = jit(p, 4) - 0.5, tn = b.shape.tint;
      hr = tn[0] * v * (1 + w * 0.16); hg = tn[1] * v * (1 + w * 0.04); hb = tn[2] * v * (1 - w * 0.2);
    }
    for (let k = 0; k < meshes.length; k++) {
      const im = meshes[k];
      im.setMatrixAt(n, _m);
      const a = im.instanceColor!.array as Float32Array, fol = b.shape && k === meshes.length - 1;
      a[n * 3] = cr * (fol ? hr : 1); a[n * 3 + 1] = cg * (fol ? hg : 1); a[n * 3 + 2] = cb * (fol ? hb : 1);
    }
  }

  /** camera = the view camera (last frame's is fine): only props in chunks under its ground footprint (plus the shadow
   *  camera's reach around the hero on high) are drawn. No camera / ?cull=0 = all of them. */
  sync(sim: Sim, t: number, camera?: THREE.PerspectiveCamera): void {
    const props = sim.terrain.props;
    if (this.gridProps !== props || this.grid.moved(props.length, this.px, this.pz)) { this.gridProps = props; this.grid.build(props.length, this.px, this.pz); }
    let box: GroundBox | null = null;
    if (camera && !CULL.off) box = viewBox(camera, CULL.margin, this.box, sim.player.x, sim.player.z, this.shadows ? shadowReach() : 0);
    this.grid.select(box);
    const G = this.grid; let considered = 0, drawn = 0;
    for (const b of this.batches) { b.n = 0; b.gn = 0; }
    // cutaway ghosts: which props may be cut this frame (camera / focus / zone of the last frame; the test has slack)
    const dt = Math.min(0.1, Math.max(0, t - this.lastT)); this.lastT = t;
    const cam = camera ? camera.position : null, F = cutFocus.value as THREE.Vector3, Z = cutZone.value as THREE.Vector4;
    const prevA = this.cutA, nextA = this.cutB; nextA.clear(); this.ghostList.length = 0;
    for (let v = 0; v < G.visN; v++) for (let q = G.chunkStart(G.vis[v]), qe = G.chunkEnd(G.vis[v]); q < qe; q++) { // each live prop in a visible chunk goes to its kind + variant set
      const p = props[G.item(q)]; considered++;
      if (!p.alive) continue;
      // fine test inside a visible chunk: the prop's footprint (tall spires / trees reach a little further) must touch the box
      if (box && !inBox(box, p.x, p.z, p.r * 1.5 + 2)) continue;
      drawn++;
      const b = this.byKind.get(p.kind)?.[p.v] ?? this.byKind.get(p.kind)?.[0]; if (!b) continue;
      const n = b.n; if (n >= b.meshes[0].instanceMatrix.count) continue;
      this.matrixFor(p, t);
      if (b.gd && cam) {
        const a = easeStep(prevA.get(p) ?? 0, this.mayCut(b, p, cam, F, Z), dt);
        if (a > 0) {
          nextA.set(p, a);
          if (b.gn < b.gd.cap) { b.gn++; this.ghostList.push({ p, b, depth: (1 - GHOST_MIN[b.kind]) * easeShape(a), dist: (p.x - cam.x) ** 2 + (p.z - cam.z) ** 2 }); continue; }
        }
      }
      this.writeInstance(b.meshes, n, p, b, t);
      b.n = n + 1;
    }
    this.cutA = nextA; this.cutB = prevA;
    // ghosts far → near, so overlapping see-through props blend in order
    if (this.ghostList.length) {
      this.ghostList.sort((x, y) => y.dist - x.dist);
      for (const b of this.batches) b.gn = 0;
      for (const g of this.ghostList) {
        const b = g.b, k = b.gn++;
        this.matrixFor(g.p, t); this.writeInstance(b.ghosts, k, g.p, b, t); b.gd!.arr[k] = g.depth;
      }
    }
    // a variant that is out of view at the start would compile its pipelines (a WebGL2 program link stall) the first time it
    // walks into view: draw one of each not-yet-drawn set once, wherever it stands, so every set compiles at run start
    for (const b of this.batches) {
      if (b.warm) continue;
      if (b.n > 0) { b.warm = true; continue; }
      const rep = props.find((p) => p.alive && p.kind === b.kind && (this.byKind.get(p.kind)?.[p.v] ?? this.byKind.get(p.kind)?.[0]) === b);
      if (!rep) continue;
      this.matrixFor(rep, t); for (const im of b.meshes) im.setMatrixAt(0, _m);
      b.n = 1; b.warm = true;
    }
    this.cullStats.considered = considered; this.cullStats.drawn = drawn; this.cullStats.total = props.length;
    CULL.stats.chunks = G.visN; CULL.stats.total = G.chunks; CULL.stats.items = considered;
    for (const b of this.batches) {
      const n = b.n;
      for (const im of b.meshes) { im.count = n; im.visible = n > 0; im.instanceMatrix.needsUpdate = true; im.instanceColor!.needsUpdate = true; }
      for (const o of b.outlines) { o.count = n; o.visible = n > 0; }
      const gn = b.gn;
      for (const im of b.ghosts) { setGhostCount(im, gn); if (gn) im.instanceColor!.needsUpdate = true; }
      for (const o of b.ghostInk) { o.count = gn; o.visible = gn > 0; }
      if (gn && b.gd) b.gd.attr.needsUpdate = true;
    }
    // carve craters / char scorches into the ground map (each mark once; merged craters re-stamp when they grow)
    const now = sim.time;
    for (const cr of sim.terrain.craters) {
      const key = cr as unknown as { _r?: number };
      if (key._r !== cr.r) { key._r = cr.r; this.ground.stampCrater(cr.x, cr.z, cr.r, cr.deep ?? 1); }
    }
    let en = 0;
    const ecol = this.embers.instanceColor!.array as Float32Array;
    for (const sc of sim.terrain.scorches) {
      if (!this.stamped.has(sc)) { this.stamped.add(sc); this.ground.stampBurn(sc.x, sc.z, sc.r); }
      const age = now - sc.born * DT;
      // RIFTLINE-VFX-01: Riftline's lane scorches keep the char mark but not the broad ember glow (riftline.ts draws its own cooling cracks)
      if (age < 3 && !(sc as { _rift?: boolean })._rift && (!box || inBox(box, sc.x, sc.z, sc.r))) { // BIGMAP: only embers in view
        const r = sc.r * Math.min(1, 0.4 + age * 6);
        const heat = (1 - age / 3) ** 2 * (0.85 + Math.sin(t * 9 + sc.x) * 0.15);
        _e.set(0, sc.rot, 0); _q.setFromEuler(_e);
        _p.set(sc.x, this.ground.sample(sc.x, sc.z) + 0.08, sc.z); _s.set(r * 0.85, 1, r * 0.85); _m.compose(_p, _q, _s);
        this.embers.setMatrixAt(en, _m);
        if ((sc as { _zen?: boolean })._zen) { ecol[en * 3] = 0.1 * heat; ecol[en * 3 + 1] = 0.75 * heat; ecol[en * 3 + 2] = 1.3 * heat; } // Zenith (owner 2026-11-02 palette/rocks): cyan heat (tagged by zenithFx.ts)
        else { ecol[en * 3] = 2.2 * heat; ecol[en * 3 + 1] = 1.1 * heat; ecol[en * 3 + 2] = 0.35 * heat; }
        en++;
      }
    }
    this.embers.count = en; this.embers.instanceMatrix.needsUpdate = true; this.embers.instanceColor!.needsUpdate = true;
  }

  /** World transform of a prop as currently drawn (debris spawns from this). */
  transformOf(p: Prop, out: THREE.Matrix4): THREE.Matrix4 { this.matrixFor(p, 0); return out.copy(_m); }

  private matrixFor(p: Prop, t: number): void {
    const sh = p.shake > 0 ? Math.sin(t * 60 + p.x) * p.shake * 0.5 : 0;
    const dmg = 1 - p.hp / p.maxHp;
    switch (p.kind) {
      case 'tree': { // variant shape + per-tree width jitter, height factor (sim h) and a slight lean (a few lean hard)
        _e.set(sh * 0.6, p.rot, sh * 0.4); const S = p.s * TREE_K, w = 0.9 + 0.2 * jit(p, 1);
        _s.set(S * w, S * p.h, S * w); _p.set(p.x, 0, p.z);
        const j = jit(p, 2), lean = p.v === 1 ? 0.03 * j : j < 0.14 ? 0.1 + 0.5 * j : 0.045 * jit(p, 5), la = jit(p, 6) * 6.283;
        _q.setFromEuler(_e); _q2.setFromAxisAngle(_ax.set(Math.cos(la), 0, Math.sin(la)), lean); _q.premultiply(_q2);
        _p.y += this.ground.sample(p.x, p.z); _m.compose(_p, _q, _s); return;
      }
      case 'bush': { // hit: a quick squash-wobble instead of a shake
        _e.set(sh * 0.5, p.rot, sh * 0.3); const w = 0.9 + 0.2 * jit(p, 1), B = p.s * BUSH_K;
        _s.set(B * w * (1 + sh * 0.3), B * p.h * (1 - sh * 0.4), B * w * (1 + sh * 0.3)); _p.set(p.x, -0.06, p.z); break;
      }
      case 'rock': _e.set(p.rot, p.rot * 2, 0); _s.set(p.r * 1.2, p.r * 0.85 * p.h, p.r * 1.1); _p.set(p.x + sh, p.r * 0.3, p.z); break;
      case 'boulder': _e.set(p.rot * 0.3, p.rot, 0); _s.set(p.r * 1.05, p.r * 0.8 * p.h, p.r); _p.set(p.x + sh, p.r * 0.35, p.z); break;
      case 'spire': {
        // spires lean and sink as they take damage before collapsing
        _e.set(dmg * 0.12 * Math.sin(p.rot), p.rot, dmg * 0.12 * Math.cos(p.rot) + sh * 0.05);
        _s.set(p.r * 1.45, p.h * SPIRE_K * 0.82 * (1 - dmg * 0.25), p.r * 1.3); // squatter mesa proportions (render-only)
        _p.set(p.x + sh * 0.6, -dmg * 2.5, p.z); break;
      }
    }
    // on a slope, sit at the lowest ground under the footprint (the uphill side sinks in instead of the downhill side floating)
    { const g = this.ground, x = p.x, z = p.z, d = p.r * 0.85;
      _p.y += Math.min(g.sample(x, z), g.sample(x + d, z), g.sample(x - d, z), g.sample(x, z + d), g.sample(x, z - d)); }
    _q.setFromEuler(_e);
    _m.compose(_p, _q, _s);
  }

  /** Destruction feedback for terrain events. With the physics debris running, the debris system (debris.ts) owns the
   *  dust cloud, gravel and leaves per break style; this adds only the fine glints and the ground ring. */
  onEvent(ev: { t: string; i?: number; x?: number; z?: number; r?: number; src?: number }, sim: Sim, fx: Fx, physical = false): void {
    if (ev.t === 'crater') {
      fx.particles.emit(ev.x!, 0.4, ev.z!, Math.min(28, 6 + ev.r! * 3), ev.r! * 2.2, 0x9a7a56, 0.9 + ev.r! * 0.08, 0.9, ev.r! * 1.2, 6, 2, 0.9);
      return;
    }
    const p = sim.terrain.props[ev.i!]; if (!p) return;
    const col = this.dust[p.kind] ?? (isFlora(p.kind) ? 0x5abf4a : p.kind === 'spire' ? 0xd6aa78 : 0xb8a080), bush = p.kind === 'bush';
    if (ev.t === 'propHit') {
      fx.shatter(p.x, p.kind === 'tree' ? 2 * TREE_K : bush ? 0.6 * p.s * BUSH_K : p.r * 0.6, p.z, p.kind === 'spire' ? 5 : 2, bush ? 3 : 5, col, bush ? 0.25 : 0.35);
      return;
    }
    // propBreak
    const big = p.kind === 'spire' ? 3 : p.kind === 'boulder' ? 1.6 : bush ? 0.5 : 1;
    const y = p.kind === 'tree' ? 2.2 * TREE_K : bush ? 0.6 * p.s * BUSH_K : p.kind === 'spire' ? p.h * SPIRE_K * 0.4 : p.r * 0.5;
    if (physical) {
      fx.shatter(p.x, y * 0.6, p.z, Math.round(3 * big), 7 * big, col, 0.25 * big);
      if (!bush && (ev.src === 1 || ev.src === 5)) fx.rings.add(p.x, 0.15, p.z, p.r * 2.5 + 2, 0.5, 0xe0d0b0, 0.8, 1); // blast / trample shock ring
      return;
    }
    fx.shatter(p.x, y, p.z, Math.round(10 * big), 9 * big, col, 0.55 * big);
    if (p.kind === 'tree') fx.shatter(p.x, 2.2 * TREE_K, p.z, 6, 5, 0x5abf4a, 0.45);
    fx.particles.emit(p.x, y * 0.5, p.z, Math.round(14 * big), 5 * big, 0xc8b090, 1.1 * big, 1.1, 2 * big, 0, 2, 0.8);
    fx.rings.add(p.x, 0.15, p.z, p.r * 2.5 + 2, 0.5, 0xe0d0b0, 1.2, 1);
    if (p.kind === 'spire') { fx.shake = Math.min(1, fx.shake + 0.6); fx.stars.add(p.x, y, p.z, p.r * 2.4, 0.3, 0xfff0d0, 2); }
    else if (p.kind === 'boulder') fx.shake = Math.min(1, fx.shake + 0.2);
  }
}
